import { CdkTrapFocus } from '@angular/cdk/a11y';
import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, model, output, signal } from '@angular/core';
import { ArrowRight, Search } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { IconNode } from '../../icon/icon.types';
import { injectNbUiText } from '../../i18n/ui-text';

export interface CommandItem {
  id: string;
  label: string;
  /** Secondary line ("Gå til side", "Leil. 304 · Eier"). */
  sub?: string;
  /** Group/kind shown as a small tag ("Side", "Handling", "Sak", "Beboer", "Dokument"). */
  kind?: string;
  icon?: IconNode;
  /** Extra search terms not shown. */
  keywords?: string;
}

/**
 * `<nb-command-palette>` — ⌘K / Ctrl+K palette (2026-09 styre prototype).
 * Searches pages, actions, saker, beboere and dokumenter; shows at most 9 hits.
 *
 * Keyboard: ⌘K/Ctrl+K toggles (document-wide), ArrowUp/Down move, Enter picks,
 * Escape closes. A11y: the input is a `combobox` with `aria-expanded`,
 * `aria-controls` and `aria-activedescendant`; results are a `listbox` of
 * `option`s only (the empty state renders as a sibling paragraph, never inside
 * the listbox); the hit count is announced through a polite live region; focus
 * is trapped while open and returned to the opener on close. `placeholder`,
 * `ariaLabel` and `emptyText` default to Norwegian — pass translated strings
 * from the app.
 *
 * Data comes from the host (`items`) — combine page routes with API results: listen to
 * `queryChange` (debounce in the host), append the API hits to `items` and flip `loading`
 * while the requests are in flight (renders a `role="status"` line under the list).
 *
 * @example
 * <nb-command-palette [items]="cmdItems()" [(open)]="cmdOpen" [loading]="searching()" (queryChange)="onQuery($event)" (picked)="run($event)" />
 */
@Component({
  selector: 'nb-command-palette',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon, CdkTrapFocus],
  host: { '(document:keydown)': 'onDocKey($event)' },
  template: `@if (open()) {
    <button type="button" class="scrim" tabindex="-1" aria-label="Lukk kommandopaletten" (click)="close()"></button>
    <div class="palette" cdkTrapFocus [cdkTrapFocusAutoCapture]="true" role="dialog" aria-modal="true" [attr.aria-label]="labelText()">
      <div class="box">
        <nb-icon [icon]="Search" />
        <input
          #inp
          type="text"
          role="combobox"
          autocomplete="off"
          spellcheck="false"
          [placeholder]="placeholderText()"
          [attr.aria-label]="placeholderText()"
          aria-autocomplete="list"
          [attr.aria-expanded]="open()"
          [attr.aria-controls]="listId"
          [attr.aria-activedescendant]="results().length ? optionId(activeIndex()) : null"
          [value]="query()"
          (input)="onInput($any($event.target).value)"
          (keydown)="onKey($event)"
        />
        <kbd aria-hidden="true">Esc</kbd>
      </div>
      <p class="nb-sr-only" aria-live="polite">{{ countText() }}</p>
      <ul class="list" role="listbox" [id]="listId" [attr.aria-label]="labelText()">
        @for (item of results(); track item.id; let i = $index) {
          <!-- Combobox pattern (WAI-ARIA APG): focus stays in the input; options are
               reached with ArrowUp/Down + Enter via aria-activedescendant, so they are
               intentionally not focusable and the click is a pointer convenience. -->
          <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
          <li
            role="option"
            [id]="optionId(i)"
            class="item"
            [class.active]="i === activeIndex()"
            [attr.aria-selected]="i === activeIndex()"
            (mousemove)="activeIndex.set(i)"
            (click)="pick(item)"
          >
            <span class="ico"><nb-icon [icon]="item.icon ?? ArrowRight" /></span>
            <span class="txt">
              <span class="lbl">{{ item.label }}</span>
              @if (item.sub) {
                <span class="sub">{{ item.sub }}</span>
              }
            </span>
            @if (item.kind) {
              <span class="kind">{{ item.kind }}</span>
            }
          </li>
        }
      </ul>
      @if (!results().length) {
        <p class="empty">{{ emptyLabel() }}</p>
      }
      @if (loading()) {
        <p class="loading" role="status">{{ loadingLabel() }}</p>
      }
    </div>
  }`,
  styleUrl: './command-palette.css',
})
export class NbCommandPalette {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Everything searchable. */
  readonly items = input.required<CommandItem[]>();
  /** Two-way open state. */
  readonly open = model(false);
  readonly placeholder = input<string | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly emptyText = input<string | undefined>(undefined);
  /** Maximum hits shown (prototype: 9). */
  readonly limit = input(9);
  /** True while the host is fetching API hits — renders `loadingText` as a status line. */
  readonly loading = input(false);
  readonly loadingText = input<string | undefined>(undefined);
  /** Emits the chosen item; the palette closes itself. */
  readonly picked = output<CommandItem>();
  /** The raw search text on every keystroke, and `''` when the palette closes. */
  readonly queryChange = output<string>();

  private readonly uiText = injectNbUiText();
  /** Rendered chrome — the inputs when given, else the app's design-system copy. */
  protected readonly placeholderText = computed(() => this.placeholder() ?? this.uiText().commandPlaceholder);
  protected readonly labelText = computed(() => this.ariaLabel() ?? this.uiText().commandPalette);
  protected readonly emptyLabel = computed(() => this.emptyText() ?? this.uiText().noHits);
  protected readonly loadingLabel = computed(() => this.loadingText() ?? this.uiText().searching);
  protected readonly query = signal('');
  protected readonly activeIndex = signal(0);
  protected readonly listId = `nb-cmd-list-${nextId++}`;
  protected readonly Search = Search;
  protected readonly ArrowRight = ArrowRight;
  private opener: HTMLElement | null = null;

  protected readonly results = computed(() => {
    const q = this.query().trim().toLowerCase();
    const all = this.items();
    const hits = q
      ? all.filter((it) => `${it.label} ${it.sub ?? ''} ${it.kind ?? ''} ${it.keywords ?? ''}`.toLowerCase().includes(q))
      : all;
    return hits.slice(0, this.limit());
  });
  protected readonly countText = computed(() => {
    const n = this.results().length;
    const t = this.uiText();
    return this.open() ? (n === 1 ? t.hitsOne : t.hitsMany.replace('{count}', String(n))) : '';
  });

  constructor() {
    // Reset the search each time the palette opens and remember the opener for focus restore.
    afterRenderEffect(() => {
      if (this.open()) {
        this.opener ??= document.activeElement as HTMLElement | null;
        this.host.nativeElement.querySelector<HTMLInputElement>('input')?.focus();
      } else if (this.opener) {
        const o = this.opener;
        this.opener = null;
        this.query.set('');
        this.activeIndex.set(0);
        this.queryChange.emit('');
        o.focus?.();
      }
    });
  }

  protected optionId(i: number): string {
    return `${this.listId}-opt-${i}`;
  }

  protected onInput(v: string): void {
    this.query.set(v);
    this.activeIndex.set(0);
    this.queryChange.emit(v);
  }

  protected onDocKey(e: KeyboardEvent): void {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.open.set(!this.open());
    } else if (e.key === 'Escape' && this.open()) {
      e.preventDefault();
      this.close();
    }
  }

  protected onKey(e: KeyboardEvent): void {
    const n = this.results().length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (n) this.activeIndex.set((this.activeIndex() + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (n) this.activeIndex.set((this.activeIndex() - 1 + n) % n);
        break;
      case 'Enter': {
        e.preventDefault();
        const it = this.results()[this.activeIndex()];
        if (it) this.pick(it);
        break;
      }
    }
  }

  protected pick(item: CommandItem): void {
    this.picked.emit(item);
    this.close();
  }

  close(): void {
    this.open.set(false);
  }
}

let nextId = 0;
